import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Alert, IconButton, Tooltip, TextField, InputAdornment,
  CircularProgress, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Divider, Grid
} from '@mui/material';
import { Search, People, Home, AttachMoney, Edit, Save, Close, Receipt } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmtN, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { Active: 'success', Probation: 'info', 'On Leave': 'warning', Terminated: 'error' };

const PayrollEmployeeList = () => {
  const navigate = useNavigate();

  // ── List state ──
  const [employees,  setEmployees]  = useState([]);
  const [grades,     setGrades]     = useState([]);
  const [payGroups,  setPayGroups]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [filterGrp,  setFilterGrp]  = useState('');
  const [filterDept, setFilterDept] = useState('');

  // ── Edit dialog state ──
  const [selected,   setSelected]   = useState(null);   // employee being edited
  const [steps,      setSteps]      = useState([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [form,       setForm]       = useState({});
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(selected), value: form, onClose: () => setSelected(null) });
  const [saving,     setSaving]     = useState(false);
  const [dlgError,   setDlgError]   = useState('');
  const [dlgSuccess, setDlgSuccess] = useState('');

  // ── Payslip history dialog ──
  const [slipEmp,    setSlipEmp]    = useState(null);   // employee whose payslips are shown
  const [slips,      setSlips]      = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);

  const openPayslips = async (emp) => {
    setSlipEmp(emp);
    setSlips([]);
    setSlipsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/payroll/employees/${emp.employeeNo}/entries`, { headers: hdrs() });
      setSlips(data || []);
    } catch { setSlips([]); }
    finally { setSlipsLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, gradeRes, grpRes] = await Promise.all([
        axios.get(`${API}/api/employees`, { headers: hdrs() }),
        axios.get(`${API}/api/employees/pay-grades/all`, { headers: hdrs() }),
        axios.get(`${API}/api/employees/pay-groups`, { headers: hdrs() }),
      ]);
      setEmployees(empRes.data || []);
      setGrades(gradeRes.data || []);
      setPayGroups(grpRes.data || []);
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load steps when grade changes in dialog
  const loadSteps = async (gradeCode) => {
    if (!gradeCode) { setSteps([]); return; }
    setStepsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/employees/pay-grades/${gradeCode}/steps`, { headers: hdrs() });
      setSteps(data || []);
    } catch { setSteps([]); }
    finally { setStepsLoading(false); }
  };

  // Open edit dialog
  const openEdit = async (emp) => {
    setSelected(emp);
    setForm({
      payGroupCode: emp.payGroupCode || '',
      payGradeCode: emp.payGradeCode || '',
      payStepNo:    emp.payStepNo != null ? String(emp.payStepNo) : '',
      basicSalary:  emp.basicSalary ?? '',
    });
    setDlgError(''); setDlgSuccess('');
    await loadSteps(emp.payGradeCode);
  };

  const handleGradeChange = async (gradeCode) => {
    setForm(f => ({ ...f, payGradeCode: gradeCode, payStepNo: '', basicSalary: '' }));
    await loadSteps(gradeCode);
  };

  const handleStepChange = (stepNo) => {
    const step = steps.find(s => String(s.stepNo) === String(stepNo));
    setForm(f => ({
      ...f,
      payStepNo:   String(stepNo),
      basicSalary: step ? step.salary : f.basicSalary,
    }));
  };

  const handleSave = async () => {
    setDlgError('');
    const grade = grades.find(g => g.gradeCode === form.payGradeCode);
    const sal   = Number(form.basicSalary);
    if (form.payGradeCode && grade) {
      if (grade.minSalary != null && sal < grade.minSalary)
        return setDlgError(`Basic salary ${sym()} ${fmtN(sal)} is below the grade minimum of ${sym()} ${fmtN(grade.minSalary)}`);
      if (grade.maxSalary != null && sal > grade.maxSalary)
        return setDlgError(`Basic salary ${sym()} ${fmtN(sal)} exceeds the grade maximum of ${sym()} ${fmtN(grade.maxSalary)}`);
    }
    setSaving(true);
    try {
      const payload = {
        payGroupCode: form.payGroupCode || null,
        payGradeCode: form.payGradeCode || null,
        payStepNo:    form.payStepNo !== '' ? Number(form.payStepNo) : null,
        basicSalary:  sal || null,
      };
      const { data } = await axios.put(`${API}/api/employees/${selected.employeeNo}`, payload, { headers: hdrs() });
      // Update local list
      setEmployees(prev => prev.map(e => e.employeeNo === data.employeeNo ? { ...e, ...data } : e));
      setSelected(prev => ({ ...prev, ...data }));
      setDlgSuccess('Saved');
      setTimeout(() => setDlgSuccess(''), 2500);
    } catch (err) { setDlgError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Derived filter options
  const depts = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();
  const grpOptions = [...new Set(employees.map(e => e.payGroupCode).filter(Boolean))].sort();

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
      e.employeeNo?.toLowerCase().includes(q) ||
      e.jobTitle?.toLowerCase().includes(q);
    return matchSearch &&
      (!filterGrp  || e.payGroupCode === filterGrp) &&
      (!filterDept || e.department   === filterDept);
  });

  // For the selected employee's grade range display
  const selectedGrade = grades.find(g => g.gradeCode === form.payGradeCode);
  const salNum = Number(form.basicSalary);
  const salOutOfRange = form.basicSalary !== '' && selectedGrade && (
    (selectedGrade.minSalary != null && salNum < selectedGrade.minSalary) ||
    (selectedGrade.maxSalary != null && salNum > selectedGrade.maxSalary)
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Payroll Home">
              <IconButton size="small" onClick={() => navigate('/payroll')}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home fontSize="small" />
              </IconButton>
            </Tooltip>
            <People />
            <Typography variant="h6" fontWeight={700}>Payroll Employees</Typography>
            <Chip label={`${filtered.length} shown`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField placeholder="Search name, no, job title…" size="small" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <TextField select label="Pay Group" size="small" value={filterGrp}
              onChange={e => setFilterGrp(e.target.value)} sx={{ minWidth: 140 }}
              InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value=""><em>All Groups</em></MenuItem>
              {grpOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </TextField>
            <TextField select label="Department" size="small" value={filterDept}
              onChange={e => setFilterDept(e.target.value)} sx={{ minWidth: 160 }}
              InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {depts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card elevation={2}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell><strong>Pay Group</strong></TableCell>
                  <TableCell><strong>Grade / Step</strong></TableCell>
                  <TableCell align="right"><strong>Basic Salary</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        {employees.length === 0 ? 'No employees found.' : 'No employees match the filter.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(e => (
                  <TableRow key={e.employeeNo} hover sx={{ cursor: 'pointer', opacity: e.status === 'Terminated' ? 0.5 : 1 }}
                    onClick={() => openEdit(e)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{e.firstName} {e.lastName}</Typography>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{e.employeeNo}</Typography>
                      {e.jobTitle && <Typography variant="caption" color="text.secondary" display="block">{e.jobTitle}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2">{e.department || '—'}</Typography></TableCell>
                    <TableCell>
                      {e.payGroupCode
                        ? <Chip label={e.payGroupCode} size="small" variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      {e.payGradeCode ? (
                        <Typography variant="body2" fontFamily="monospace">
                          {e.payGradeCode}{e.payStepNo != null ? `  /  Step ${e.payStepNo}` : ''}
                        </Typography>
                      ) : <Typography variant="caption" color="warning.main">Not set</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {e.basicSalary ? `${sym()} ${fmtN(e.basicSalary)}` : <span style={{ color: '#bbb' }}>—</span>}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={e.status} size="small" color={STATUS_COLOR[e.status] || 'default'} />
                    </TableCell>
                    <TableCell align="center" onClick={ev => ev.stopPropagation()}>
                      <Tooltip title="Edit Payroll Info">
                        <IconButton size="small" color="primary" onClick={() => openEdit(e)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Pay Lines">
                        <IconButton size="small" color="secondary"
                          onClick={() => navigate(`/hr/employees/${e.employeeNo}?context=payroll&from=/payroll/employees`)}>
                          <AttachMoney fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Payslip History">
                        <IconButton size="small" sx={{ color: '#37474f' }} onClick={() => openPayslips(e)}>
                          <Receipt fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Payslip History Dialog ── */}
      <Dialog open={Boolean(slipEmp)} onClose={() => setSlipEmp(null)} maxWidth="md" fullWidth>
        {slipEmp && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <Receipt fontSize="small" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                      {slipEmp.firstName} {slipEmp.lastName} — Payslip History
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slipEmp.employeeNo}{slipEmp.department ? ` · ${slipEmp.department}` : ''}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setSlipEmp(null)}><Close fontSize="small" /></IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
              {slipsLoading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : slips.length === 0 ? (
                <Box textAlign="center" py={5} color="text.secondary">
                  <Receipt sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                  <Typography variant="body2">No payslips found for this employee.</Typography>
                  <Typography variant="caption">Payslips appear here after a payroll run is calculated.</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Run</strong></TableCell>
                      <TableCell><strong>Period</strong></TableCell>
                      <TableCell align="right"><strong>Gross Pay</strong></TableCell>
                      <TableCell align="right"><strong>PAYE</strong></TableCell>
                      <TableCell align="right"><strong>Deductions</strong></TableCell>
                      <TableCell align="right"><strong>Net Pay</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slips.map(ps => (
                      <TableRow key={ps._id} hover>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace" fontWeight={600}>{ps.runNo}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{ps.periodName || ps.periodCode}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{sym()} {fmtN(ps.grossPay)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main">{sym()} {fmtN(ps.paye)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main">{sym()} {fmtN(ps.totalDeductions)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color="success.main">{sym()} {fmtN(ps.netPay)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={ps.isPosted ? 'Posted' : 'Draft'} size="small"
                            color={ps.isPosted ? 'success' : 'default'} sx={{ height: 18, fontSize: 10 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Payslip">
                            <IconButton size="small" color="primary"
                              onClick={() => { setSlipEmp(null); navigate(`/payroll/runs/${ps.runNo}/payslip/${slipEmp.employeeNo}`); }}>
                              <Receipt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 2, py: 1 }}>
              <Button size="small" startIcon={<AttachMoney fontSize="small" />}
                onClick={() => { setSlipEmp(null); navigate(`/hr/employees/${slipEmp.employeeNo}?context=payroll&from=/payroll/employees`); }}>
                Pay Lines
              </Button>
              <Box flex={1} />
              <Button size="small" onClick={() => setSlipEmp(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Edit Payroll Info Dialog ── */}
      {discardDialog}
      <Dialog open={Boolean(selected)} onClose={attemptClose} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                    {selected.firstName} {selected.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selected.employeeNo}
                    {selected.jobTitle ? ` · ${selected.jobTitle}` : ''}
                    {selected.department ? ` · ${selected.department}` : ''}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label={selected.status} size="small" color={STATUS_COLOR[selected.status] || 'default'} />
                  <IconButton size="small" onClick={() => setSelected(null)}><Close fontSize="small" /></IconButton>
                </Box>
              </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
              {dlgError   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setDlgError('')}>{dlgError}</Alert>}
              {dlgSuccess && <Alert severity="success" sx={{ mb: 2 }}>{dlgSuccess}</Alert>}

              <Grid container spacing={2}>

                {/* Pay Group */}
                <Grid item xs={12}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    textTransform="uppercase" letterSpacing={0.8}>Pay Group</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField select label="Pay Group" size="small" fullWidth
                    value={form.payGroupCode}
                    onChange={e => setForm(f => ({ ...f, payGroupCode: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    helperText="Group used to filter payroll runs">
                    <MenuItem value=""><em>— None —</em></MenuItem>
                    {payGroups.map(g => (
                      <MenuItem key={g.groupCode} value={g.groupCode}>
                        {g.groupCode} — {g.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Grade & Step */}
                <Grid item xs={12}>
                  <Divider>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      textTransform="uppercase" letterSpacing={0.8}>Grade &amp; Step</Typography>
                  </Divider>
                </Grid>

                <Grid item xs={6}>
                  <TextField select label="Pay Grade" size="small" fullWidth
                    value={form.payGradeCode}
                    onChange={e => handleGradeChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    helperText={selectedGrade ? `${sym()} ${fmtN(selectedGrade.minSalary)} – ${sym()} ${fmtN(selectedGrade.maxSalary)}` : 'Select a grade'}>
                    <MenuItem value=""><em>— None —</em></MenuItem>
                    {grades.filter(g => g.isActive !== false).map(g => (
                      <MenuItem key={g.gradeCode} value={g.gradeCode}>
                        {g.gradeCode} — {g.description}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField select label="Pay Step" size="small" fullWidth
                    value={form.payStepNo}
                    onChange={e => handleStepChange(e.target.value)}
                    disabled={!form.payGradeCode || stepsLoading}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    helperText={stepsLoading ? 'Loading steps…' : steps.length === 0 && form.payGradeCode ? 'No steps configured for this grade' : 'Salary auto-fills on selection'}>
                    <MenuItem value=""><em>— No step —</em></MenuItem>
                    {steps.map(s => (
                      <MenuItem key={s.stepNo} value={String(s.stepNo)}>
                        Step {s.stepNo}{s.label && s.label !== `Step ${s.stepNo}` ? ` — ${s.label}` : ''}
                        &nbsp;&nbsp;({sym()} {fmtN(s.salary)})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Basic Salary */}
                <Grid item xs={12}>
                  <Divider>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      textTransform="uppercase" letterSpacing={0.8}>Basic Salary</Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={`Basic Salary (${sym()})`}
                    type="number"
                    size="small"
                    fullWidth
                    value={form.basicSalary}
                    onChange={e => setForm(f => ({ ...f, basicSalary: e.target.value }))}
                    error={salOutOfRange}
                    helperText={
                      salOutOfRange
                        ? `Must be within ${sym()} ${fmtN(selectedGrade.minSalary)} – ${sym()} ${fmtN(selectedGrade.maxSalary)}`
                        : selectedGrade
                          ? `Grade range: ${sym()} ${fmtN(selectedGrade.minSalary)} – ${sym()} ${fmtN(selectedGrade.maxSalary)}${selectedGrade.midpoint ? `  ·  Midpoint: ${sym()} ${fmtN(selectedGrade.midpoint)}` : ''}`
                          : 'Auto-filled from step, or enter manually'
                    }
                    inputProps={{ min: selectedGrade?.minSalary ?? 0, max: selectedGrade?.maxSalary ?? undefined, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{sym()}</InputAdornment>,
                    }}
                  />
                </Grid>

              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                size="small"
                startIcon={<AttachMoney fontSize="small" />}
                onClick={() => {
                  setSelected(null);
                  navigate(`/hr/employees/${selected.employeeNo}?context=payroll&from=/payroll/employees`);
                }}>
                Pay Lines
              </Button>
              <Box flex={1} />
              <Button onClick={attemptClose}>Cancel</Button>
              <Button variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                onClick={handleSave} disabled={saving || salOutOfRange}
                sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PayrollEmployeeList;
